import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";

/* hs-eslint ignored failing-rules */

/* eslint-disable react-hooks/exhaustive-deps */
import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import { calculateAdditionalUnitsForTierSwitch, getMaxAdditionalUnits, getQuantityPackSteppingFunction } from 'pricing-pages-lib/utils/contactsUtils';
import { getIsUngatedForSeats } from 'pricing-pages-lib/utils/gatingUtils';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { useEffect, useId, useState } from 'react';
import { ENTERPRISE, PROFESSIONAL } from 'self-service-api/constants/ProductLevels';
import { MARKETING } from 'self-service-api/constants/ProductLines';
import styled from 'styled-components';
import Big from 'UIComponents/elements/Big';
import Small from 'UIComponents/elements/Small';
import UIFormLabel from 'UIComponents/form/UIFormLabel';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILink from 'UIComponents/link/UILink';
import UITile from 'UIComponents/tile/UITile';
import { CALCULATOR } from '../../../constants/tracking/Locations';
import { useAppSelector } from '../../../reducers';
import { minPurchasableContactsSelector } from '../../../selectors/ContactsSelectors';
import { activeCurrencySelector } from '../../../selectors/CurrencySelectors';
import { calculatedPriceForProductConfigurationsInListSelector } from '../../../selectors/PricingSelectors';
import { hasHigherProductLevelInProductInventorySelector, hasProductInProductInventorySelector, productsFromProductInventoryQuantityPackQuantitySelector } from '../../../selectors/ProductInventorySelectors';
import { currentSkuIdForProductSelector, productSelector, productVersionSelector, skuSelector } from '../../../selectors/ProductsSelectors';
import { usePricingLibSelector } from '../../../selectors/SelectorUtils';
import { StyledStepperInput } from '../../plan-builder/SharedComponents';
import ContactsInfoBox from '../infoBox/ContactsInfoBox';
import ContactsMaxLimitContainer from '../infoBox/ContactsMaxLimitContainer';
import AsyncContactsPriceBreakdownModal from '../pricing/AsyncContactsPriceBreakdownModal';
import PriceDisplay from '../pricing/PriceDisplay';
const TTS_CONTACT_LIMIT = 500000;
const StyledInlineTitle = styled(FormattedMessage).withConfig({
  displayName: "ContactsPicker__StyledInlineTitle",
  componentId: "sc-1u5qiqz-0"
})(["font-weight:600;font-size:14px;"]);
const DescriptionContainer = styled.div.withConfig({
  displayName: "ContactsPicker__DescriptionContainer",
  componentId: "sc-1u5qiqz-1"
})(["line-height:2;font-size:12px;margin:0 auto;margin-top:8px;"]);

const ContactsPicker = ({
  quantityPackSkuId,
  quantityPackQuantity,
  onChange,
  productConfigurations,
  setProductLevel,
  trackingSource = CALCULATOR,
  queryParamTerm,
  compact
}) => {
  const isSeatsRedesignUngated = usePricingLibSelector(getIsUngatedForSeats);
  const [priceBreakdownVisible, setPriceBreakdownVisible] = useState(false);
  const quantityPack = useAppSelector(state => skuSelector(state, {
    skuId: quantityPackSkuId
  }));
  const {
    quantityUnit: {
      includedWithQuantityPack,
      includedWithProductVersion,
      unitType,
      unitScope
    },
    productId
  } = quantityPack;
  const {
    apiName,
    productLine,
    productLevel
  } = useAppSelector(state => productSelector(state, {
    productId
  }));
  const minPurchasableContacts = useAppSelector(state => minPurchasableContactsSelector(state, {
    quantityPackSkuId
  })) || 0;
  const ownedQuantityPackQuantity = useAppSelector(state => productsFromProductInventoryQuantityPackQuantitySelector(state, {
    productIdentifier: {
      apiName
    },
    unitType,
    unitScope
  }));
  const ownsProduct = useAppSelector(state => hasProductInProductInventorySelector(state, {
    productAttributeFilters: {
      productLine,
      productLevel
    }
  }));
  const ownedUnitQuantity = ownsProduct ? includedWithProductVersion + ownedQuantityPackQuantity : 0;
  const ownsHigherProductLevel = useAppSelector(state => hasHigherProductLevelInProductInventorySelector(state, {
    productLine: MARKETING,
    productLevel
  }));
  const currencyCode = useAppSelector(activeCurrencySelector);
  const maxAdditionalUnits = getMaxAdditionalUnits(quantityPack);
  const maxContactsInPlan = maxAdditionalUnits + includedWithProductVersion;
  const steppingFunction = getQuantityPackSteppingFunction(quantityPack);
  useEffect(() => {
    if (quantityPackQuantity < minPurchasableContacts && !ownsHigherProductLevel) {
      onChange(steppingFunction(minPurchasableContacts));
    } else if (steppingFunction(quantityPackQuantity) !== quantityPackQuantity) {
      onChange(steppingFunction(quantityPackQuantity));
    }
  }, [quantityPackQuantity, minPurchasableContacts]);
  useEffect(() => {
    if (priceBreakdownVisible) {
      getUsageTracker().track('clickToViewModal', {
        name: 'contact-picker-more-details-modal',
        location: trackingSource,
        productLine,
        productLevel
      });
    }
  }, [priceBreakdownVisible]);
  const enterpriseQuantityPack = useAppSelector(state => {
    const {
      quantityUnit: {
        limitName
      }
    } = quantityPack;
    const skuId = currentSkuIdForProductSelector(state, {
      productLine: MARKETING,
      productLevel: ENTERPRISE
    });
    const {
      quantityPackDetails: enterpriseQuantityPackDetails
    } = productVersionSelector(state, {
      skuId
    });
    return enterpriseQuantityPackDetails.find(({
      skuLimits
    }) => skuLimits.some(limit => limit.name === limitName));
  });
  const startingPriceForContacts = useAppSelector(state => calculatedPriceForProductConfigurationsInListSelector(state, {
    allProductConfigurations: productConfigurations,
    productConfigurations: [createProductConfig({
      skuId: quantityPackSkuId
    })],
    queryParamTerm
  })).monthly.calculatedPrice;

  const handleSwitchToEnterprise = () => {
    setProductLevel(ENTERPRISE);
    const newMarketingUnits = calculateAdditionalUnitsForTierSwitch(quantityPackQuantity, quantityPack, enterpriseQuantityPack);
    const enterpriseSteppingFunction = getQuantityPackSteppingFunction(enterpriseQuantityPack);
    onChange(enterpriseSteppingFunction(newMarketingUnits), enterpriseQuantityPack.skuId);
  };

  const minUnitsToDisplay = ownsHigherProductLevel ? includedWithProductVersion : includedWithProductVersion + minPurchasableContacts;

  const handleContactsChange = (newValue, roundUp) => {
    const additionalUnitQuantity = steppingFunction(newValue - includedWithProductVersion, roundUp);
    getUsageTracker().track('additionalUnitsPicker', {
      trackingSource,
      unitType,
      productLine,
      productLevel,
      ownsProduct,
      skuId: quantityPackSkuId,
      ownedUnitQuantity,
      displayedUnitQuantity: includedWithProductVersion + additionalUnitQuantity
    });
    onChange(additionalUnitQuantity);
  };

  const getMarketingContactsMessageLinkElement = () => {
    return {
      Link: ({
        children
      }) => /*#__PURE__*/_jsx(UILink, {
        onClick: () => setPriceBreakdownVisible(true),
        onMouseEnter: () => AsyncContactsPriceBreakdownModal.preload(),
        children: children
      })
    };
  };

  const stepperId = useId();
  const marketingContactsTitleId = useId();
  const marketingContactsDescriptionId = useId();
  const includedMarketingContactsId = useId();
  const additionalMarketingContactsId = useId();
  const stepperAriaDescribedBy = [marketingContactsTitleId, marketingContactsDescriptionId, includedMarketingContactsId, additionalMarketingContactsId].join(' ');

  if (compact) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [priceBreakdownVisible && /*#__PURE__*/_jsx(AsyncContactsPriceBreakdownModal, {
        quantityPackSkuId: quantityPackSkuId,
        marketingContactsSelected: quantityPackQuantity + includedWithProductVersion,
        handleModalClose: () => setPriceBreakdownVisible(false),
        productConfigurations: productConfigurations,
        queryParamTerm: queryParamTerm
      }), /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsxs(DescriptionContainer, {
          className: "flex-column align-start",
          children: [/*#__PURE__*/_jsx("span", {
            style: {
              fontWeight: 600,
              fontSize: '14px'
            },
            id: marketingContactsTitleId,
            children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: "pricingPage.contactsPicker.marketingContacts.title_jsx",
              elements: Object.assign({
                IncludedAdditionalUnits: () => /*#__PURE__*/_jsx(FormattedNumber, {
                  value: includedWithProductVersion
                })
              }, isSeatsRedesignUngated && getMarketingContactsMessageLinkElement())
            })
          }), /*#__PURE__*/_jsxs(UIFlex, {
            className: "text-start",
            children: [!isSeatsRedesignUngated && /*#__PURE__*/_jsx("span", {
              id: marketingContactsDescriptionId,
              children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                message: "pricingPage.contactsPicker.marketingContacts.description_jsx",
                elements: Object.assign({
                  QuantityMultiplier: () => /*#__PURE__*/_jsx(FormattedNumber, {
                    value: includedWithQuantityPack
                  }),
                  Price: () => /*#__PURE__*/_jsx(PriceDisplay, {
                    price: startingPriceForContacts,
                    currencyCode: currencyCode
                  })
                }, !isSeatsRedesignUngated && getMarketingContactsMessageLinkElement())
              })
            }), !isSeatsRedesignUngated && /*#__PURE__*/_jsx(StyledStepperInput, {
              "aria-label": I18n.text('pricingPage.contactsPicker.a11y.selectMarketingContacts'),
              "aria-describedby": stepperAriaDescribedBy,
              stepSize: includedWithQuantityPack,
              value: steppingFunction(quantityPackQuantity) + includedWithProductVersion,
              min: minUnitsToDisplay,
              max: maxContactsInPlan,
              onChange: event => handleContactsChange(parseFloat(event.target.value), true),
              "data-selenium-id": "contacts-picker-input"
            })]
          })]
        })
      })]
    });
  }

  if (isSeatsRedesignUngated) {
    const includedContacts = steppingFunction(quantityPackQuantity) + includedWithProductVersion;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [priceBreakdownVisible && /*#__PURE__*/_jsx(AsyncContactsPriceBreakdownModal, {
        quantityPackSkuId: quantityPackSkuId,
        marketingContactsSelected: quantityPackQuantity + includedWithProductVersion,
        handleModalClose: () => setPriceBreakdownVisible(false),
        productConfigurations: productConfigurations,
        queryParamTerm: queryParamTerm
      }), /*#__PURE__*/_jsxs(UIFlex, {
        direction: "row",
        justify: "between",
        children: [/*#__PURE__*/_jsx(UIFormLabel, {
          htmlFor: stepperId,
          children: /*#__PURE__*/_jsx(Big, {
            weight: "bold",
            children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: `pricingPage.contactsPicker.select_jsx`,
              elements: {
                Link: ({
                  children
                }) => /*#__PURE__*/_jsx(UILink, {
                  onClick: () => setPriceBreakdownVisible(true),
                  onMouseEnter: () => AsyncContactsPriceBreakdownModal.preload(),
                  children: children
                })
              }
            })
          })
        }), /*#__PURE__*/_jsx(StyledStepperInput, {
          id: stepperId,
          "aria-describedby": stepperAriaDescribedBy,
          stepSize: includedWithQuantityPack,
          value: includedContacts,
          min: minUnitsToDisplay,
          max: maxContactsInPlan,
          onChange: event => handleContactsChange(parseFloat(event.target.value), true),
          "data-selenium-id": "contacts-picker-input"
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: "m-bottom-4",
        children: /*#__PURE__*/_jsxs("span", {
          className: "flex-column",
          children: [/*#__PURE__*/_jsx("span", {
            id: includedMarketingContactsId,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "pricingPage.contactsPicker.marketingContacts.includedContacts",
              options: {
                includedContacts
              }
            })
          }), /*#__PURE__*/_jsx("span", {
            id: additionalMarketingContactsId,
            children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: "pricingPage.contactsPicker.marketingContacts.additionalContacts_jsx",
              elements: {
                QuantityMultiplier: () => /*#__PURE__*/_jsx(FormattedNumber, {
                  value: includedWithQuantityPack
                }),
                Price: () => /*#__PURE__*/_jsx(PriceDisplay, {
                  price: startingPriceForContacts,
                  currencyCode: currencyCode
                })
              }
            })
          })]
        })
      })]
    });
  }

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [priceBreakdownVisible && /*#__PURE__*/_jsx(AsyncContactsPriceBreakdownModal, {
      quantityPackSkuId: quantityPackSkuId,
      marketingContactsSelected: quantityPackQuantity + includedWithProductVersion,
      handleModalClose: () => setPriceBreakdownVisible(false),
      productConfigurations: productConfigurations,
      queryParamTerm: queryParamTerm
    }), /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsxs(DescriptionContainer, {
        className: "flex-column",
        children: [/*#__PURE__*/_jsx(StyledInlineTitle, {
          message: "pricingPage.contactsPicker.marketingContacts.howMany"
        }), /*#__PURE__*/_jsxs(UIFlex, {
          children: [/*#__PURE__*/_jsx("span", {
            id: marketingContactsDescriptionId,
            children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: "pricingPage.contactsPicker.marketingContacts.description_jsx",
              elements: {
                QuantityMultiplier: () => /*#__PURE__*/_jsx(FormattedNumber, {
                  value: includedWithQuantityPack
                }),
                IncludedAdditionalUnits: () => /*#__PURE__*/_jsx(FormattedNumber, {
                  value: includedWithProductVersion
                }),
                Price: () => /*#__PURE__*/_jsx(PriceDisplay, {
                  price: startingPriceForContacts,
                  currencyCode: currencyCode
                }),
                Link: ({
                  children
                }) => /*#__PURE__*/_jsx(UILink, {
                  onClick: () => setPriceBreakdownVisible(true),
                  onMouseEnter: () => AsyncContactsPriceBreakdownModal.preload(),
                  children: children
                })
              }
            })
          }), /*#__PURE__*/_jsx(StyledStepperInput, {
            "aria-label": I18n.text('pricingPage.contactsPicker.a11y.selectMarketingContacts'),
            "aria-describedby": stepperAriaDescribedBy,
            stepSize: includedWithQuantityPack,
            value: steppingFunction(quantityPackQuantity) + includedWithProductVersion,
            min: minUnitsToDisplay,
            max: maxContactsInPlan,
            onChange: event => handleContactsChange(parseFloat(event.target.value), true),
            "data-selenium-id": "contacts-picker-input"
          })]
        })]
      }), productLevel === PROFESSIONAL && /*#__PURE__*/_jsx(ContactsInfoBox, {
        titleKey: "pricingPage.contactsPicker.pricingTip.title",
        content: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "pricingPage.contactsPicker.pricingTip.marketingProTip_jsx",
          elements: {
            UILink
          },
          options: {
            onClick: handleSwitchToEnterprise
          }
        }),
        showBox: quantityPackQuantity + includedWithProductVersion >= maxContactsInPlan
      }), productLevel === ENTERPRISE && /*#__PURE__*/_jsx(ContactsInfoBox, {
        titleKey: "pricingPage.contactsPicker.upperLimitTip.title",
        content: /*#__PURE__*/_jsx(ContactsMaxLimitContainer, {}),
        showBox: quantityPackQuantity + includedWithProductVersion >= TTS_CONTACT_LIMIT
      }), /*#__PURE__*/_jsx(UITile, {
        className: "p-all-3 m-top-4",
        children: /*#__PURE__*/_jsx(Small, {
          className: "justify-center text-center",
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "pricingPage.contactsPicker.marketingContacts.includesFreeContacts"
          })
        })
      })]
    })]
  });
};

export default ContactsPicker;